import {
  createInfiniteQuery,
  type InfiniteQueryHookOptions,
} from 'react-query-kit';
import { type InfiniteData, keepPreviousData } from '@tanstack/react-query';

import type { MyQuizsetCursorSearchReq } from '@/api/gen/_model/myQuizsetCursorSearchReq';
import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { searchMyQuizsetsByCursor } from '@/api/gen/quiz-user/quiz-user.gen';
import {
  isRecoverableCursorError,
  useCursorQueryRecovery,
} from '@/shared/hooks/useCursorQueryRecovery';
import { followEmptyCursorPages } from '@/shared/utils/followEmptyCursorPages';
import { retryOnServiceUnavailable } from '@/shared/utils/queryRetryBudget';

/**
 * 내 퀴즈 목록 무효화용 키. 접두사라 프로필 목록(`['quizsets/search']`)까지 함께 걸린다 — 의도한 범위다.
 *
 * ⚠️ 이 상수를 `useFetchMyQuizesQuery.getKey()` 로 파생하지 않는다. 좁은 키가 되어 프로필 목록이 빠진다.
 *
 * 자료실은 혼합 검색(`libraryKeys`)을 쓰므로 이 키에 걸리지 않는다.
 */
export const myQuizListKey = ['quizsets/search'] as const;

export type MyQuizCursorVariables = Omit<MyQuizsetCursorSearchReq, 'cursor'>;

export interface MyQuizCursorPage {
  items: SearchQuizsetRes[];
  nextCursor?: string;
  hasNext: boolean;
  totalCount?: number;
}

type MyQuizCursorQueryOptions = InfiniteQueryHookOptions<
  MyQuizCursorPage,
  Error,
  InfiniteData<MyQuizCursorPage, string | undefined>,
  MyQuizCursorVariables,
  string | undefined
>;

/** 커서 한 페이지를 읽는다. 빈 중간 페이지는 `followEmptyCursorPages` 가 이어 받는다. */
export const fetchMyQuizCursorPage = async (
  variables: MyQuizCursorVariables,
  cursor?: string,
  signal?: AbortSignal,
): Promise<MyQuizCursorPage> => {
  const response = await followEmptyCursorPages(
    pageCursor =>
      searchMyQuizsetsByCursor(
        { ...variables, cursor: pageCursor },
        undefined,
        signal,
      ),
    cursor,
  );

  return {
    items: response.data,
    nextCursor: response.nextCursor,
    hasNext: response.hasNext,
    totalCount: response.totalCount,
  };
};

/**
 * 원본 쿼리. 키·옵션을 직접 다뤄야 하는 곳(테스트 등)만 쓰고, 화면은 아래 `useFetchMyQuizes` 를 쓴다.
 */
export const useFetchMyQuizesQuery = createInfiniteQuery<
  MyQuizCursorPage,
  MyQuizCursorVariables,
  Error,
  string | undefined
>({
  // 프로필 목록과 접두사를 공유하되, 이 쿼리만의 세그먼트로 캐시를 가른다.
  queryKey: [...myQuizListKey, 'my', 'cursor'],
  use: [retryOnServiceUnavailable],
  fetcher: (variables, { pageParam, signal }) =>
    fetchMyQuizCursorPage(variables, pageParam, signal),
  getNextPageParam: lastPage =>
    lastPage.hasNext ? lastPage.nextCursor : undefined,
  initialPageParam: undefined,
  placeholderData: keepPreviousData,
  throwOnError: error => !isRecoverableCursorError(error),
});

/**
 * 커서가 만료되면(`INVALID_CURSOR`·`EXPIRED_CURSOR`·`CURSOR_QUERY_MISMATCH`) 에러로 던지지 않고
 * 한 번만 다시 읽는다. 복구하는 동안에도 목록이 살아 있게 `isFetchingNextPage` 로 묶어 준다.
 */
export const useFetchMyQuizes = (options?: MyQuizCursorQueryOptions) => {
  const query =
    useFetchMyQuizesQuery<InfiniteData<MyQuizCursorPage, string | undefined>>(
      options,
    );
  const { isRecovering, shouldMaskError } = useCursorQueryRecovery({
    isError: query.isError,
    error: query.error,
    hasLoadedPages:
      !query.isPlaceholderData && (query.data?.pages.length ?? 0) > 0,
    refetch: query.refetch,
  });

  return {
    ...query,
    isError: query.isError && !shouldMaskError,
    isFetchingNextPage: query.isFetchingNextPage || isRecovering,
  };
};
